import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	HostListener,
	input,
	model,
	output,
	signal,
	viewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AVATAR_VARIANT, ESigningBlockType } from '@models/e-signing';
import { BlockButtonComponent } from '@ui/block-button/block-button.component';
import { CardComponent } from '@ui/card/card.component';
import { IconsModule } from '@ui/icons/icons.module';
import {
	SelectComponent,
	type SelectOption,
} from '@ui/select/select.component';
import { AVATAR_VARIANT_ORDER } from '@ui/utils/constants';
import { TOGGLE_TYPES } from 'src/app/ui/toggle/toggle.component';
import { UiModule } from 'src/app/ui/ui.module';
import { ESigningCanvasBlockComponent } from '../e-signing-canvas/e-signing-canvas-block/e-signing-canvas-block.component';

export interface ESigningFieldDetailsClickEvent {
	type: ESigningBlockType;
	recipient: SelectOption;
	variant: AVATAR_VARIANT;
}

@Component({
	selector: 'app-e-signing-field-details',
	standalone: true,
	imports: [
		SelectComponent,
		BlockButtonComponent,
		CardComponent,
		IconsModule,
		CommonModule,
		FormsModule,
		UiModule,
		MatTooltipModule,
		ESigningCanvasBlockComponent,
	],
	templateUrl: './e-signing-field-details.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ESigningFieldDetailsComponent {
	private buttonBlockList = [
		{
			iconName: 'signature',
			label: 'signature',
			type: ESigningBlockType.SIGN,
		},
		{
			iconName: 'scribble',
			label: 'initial',
			type: ESigningBlockType.INITIALS,
		},
		{
			iconName: 'calendar-due',
			label: 'date',
			type: ESigningBlockType.DATE,
		},
		{
			iconName: 'checkbox',
			label: 'checkbox',
			type: ESigningBlockType.CHECKBOX,
		},
		{
			iconName: 'text-size',
			label: 'text',
			type: ESigningBlockType.TEXT,
		},
	];

	public TOGGLE_TYPES = TOGGLE_TYPES;
	public options = input.required<SelectOption[]>();
	public drawOnly_ = model.required<boolean>();
	public selectedOverride_ = signal<SelectOption | null>(null);
	public selected_ = computed(
		() => this.selectedOverride_() ?? this.options()[0] ?? null,
	);
	public selectedBlock_ = model<ESigningFieldDetailsClickEvent>(null);
	public onButtonClick = output<ESigningFieldDetailsClickEvent>();
	public dragStart = output<ESigningFieldDetailsClickEvent>();
	public dragEnd = output<void>();
	public variant = computed(
		() =>
			AVATAR_VARIANT_ORDER[
				this.options().findIndex(
					(option) => option.id === this.selected_()?.id,
				) % AVATAR_VARIANT_ORDER.length
			],
	);
	public buttonBlockList_ = computed(() =>
		this.buttonBlockList.map((button) => ({
			...button,
			ariaLabel: `${this.selected_()?.text} ${button.label}`,
		})),
	);
	public selectedBlockInfo_ = computed(() => {
		const selectedBlock = this.selectedBlock_();
		if (!selectedBlock) return null;
		return this.buttonBlockList.find((b) => b.type === selectedBlock.type);
	});
	public selectedBlockType_ = computed(
		() => this.selectedBlock_()?.type ?? null,
	);
	public isDragging_ = signal<boolean>(false);
	public mousePosition_ = signal<{ x: number; y: number }>({ x: 0, y: 0 });
	private dragPreviews_ = viewChildren<ElementRef>('dragPreview');

	constructor(private elementRef: ElementRef) {}

	@HostListener('document:mousemove', ['$event'])
	private onMouseMove(event: MouseEvent) {
		if (!this.selectedBlock_() || this.isDragging_()) return;

		this.mousePosition_.set({ x: event.clientX, y: event.clientY });
	}

	@HostListener('document:click', ['$event'])
	private onDocumentClick(event: MouseEvent) {
		if (!this.selectedBlock_()) return;

		const target = event.target;
		if (
			target instanceof Element &&
			!this.elementRef.nativeElement.contains(target)
		) {
			this.selectedBlock_.set(null);
		}
	}

	public onBlockButtonClick(event: MouseEvent, type: ESigningBlockType): void {
		const selectedBlock = {
			type,
			recipient: this.selected_(),
			variant: this.variant(),
		};

		if (this.selectedBlock_()?.type === type) {
			this.selectedBlock_.set(null);
		} else {
			this.selectedBlock_.set(selectedBlock);
			this.mousePosition_.set({ x: event.clientX, y: event.clientY });
		}
		this.onButtonClick.emit(selectedBlock);
	}

	public onDragStart(event: DragEvent, type: ESigningBlockType): void {
		const selectedBlock = {
			type,
			recipient: this.selected_(),
			variant: this.variant(),
		};
		event.stopPropagation();

		const previewElement = this.dragPreviews_().find(
			(el) => el.nativeElement.dataset.blockType === String(type),
		)?.nativeElement;

		if (previewElement) {
			event.dataTransfer.setDragImage(previewElement, 0, 0);
		}

		this.selectedBlock_.set(selectedBlock);
		this.isDragging_.set(true);

		this.dragStart.emit(selectedBlock);
	}

	public onDragEnd(): void {
		this.isDragging_.set(false);
		this.selectedBlock_.set(null);
		this.dragEnd.emit();
	}
}
